import React, { useState } from 'react';
import { setupFirestoreCollections, checkCollectionsExist } from '../utils/setupFirestore';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

const FirestoreSetup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [collections, setCollections] = useState({});

  const handleSetupCollections = async () => {
    setIsLoading(true);
    setStatus('Setting up collections...');
    
    try {
      const success = await setupFirestoreCollections();
      
      if (success) {
        setStatus('✅ Collections created successfully!');
        // Check what was created
        const existingCollections = await checkCollectionsExist();
        setCollections(existingCollections);
      } else {
        setStatus('❌ Failed to create collections. Check console for details.');
      }
    } catch (error) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckCollections = async () => {
    setIsLoading(true);
    setStatus('Checking existing collections...');
    
    try {
      const existingCollections = await checkCollectionsExist();
      setCollections(existingCollections);
      setStatus('✅ Collection check completed!');
    } catch (error) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Firestore Setup
          </h1>
          <p className="text-gray-600">
            Set up your Firestore collections with sample data
          </p>
        </div>

        {/* Status */}
        {status && (
          <div className={`mb-4 p-3 rounded-lg ${
            status.includes('✅') 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : status.includes('❌') 
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            <div className="flex items-center gap-2">
              {status.includes('✅') && <CheckCircle className="w-4 h-4" />}
              {status.includes('❌') && <AlertCircle className="w-4 h-4" />}
              {isLoading && <Loader className="w-4 h-4 animate-spin" />}
              <span className="text-sm">{status}</span>
            </div>
          </div>
        )}

        {/* Collection Status */}
        {Object.keys(collections).length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Collection Status:</h3>
            <div className="space-y-2">
              {Object.entries(collections).map(([collectionName, exists]) => (
                <div key={collectionName} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {collectionName}
                  </span>
                  <div className="flex items-center gap-2">
                    {exists ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600">Exists</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-red-600">Missing</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleSetupCollections}
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Setting up...
              </>
            ) : (
              '🚀 Setup All Collections'
            )}
          </button>

          <button
            onClick={handleCheckCollections}
            disabled={isLoading}
            className="w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Checking...
              </>
            ) : (
              '🔍 Check Collections'
            )}
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">What this does:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Creates 5 collections: users, lawyers, appointments, chats, subscriptions</li>
            <li>• Adds sample data for testing</li>
            <li>• Sets up proper data structure</li>
            <li>• Only runs once (won't duplicate data)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FirestoreSetup;
